import { Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';

import PartyTabSection from '@/components/common/PartyTabSection';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import PolicyItem from '@/features/policies/components/PoliciesSection/PolicyItem';
import usePolicies from '@/features/policies/queries/hooks/usePolicies';
import paths from '@/lib/paths';
import hasItems from '@/lib/utils/hasItems';
import useLocalSettings from '@/stores/useLocalSettings';

import usePolicyPackages from '../../queries/hooks/usePolicyPackages';

import PolicyPackageItem from './PolicyPackageItem';

export default function PoliciesSection() {
  const { t } = useTranslation();

  const { onlyShowRelevant } = useLocalSettings();

  const party = useSelectedParty();
  const {
    analysis: {
      relevant: { policyIds, policyPackageIds },
    },
  } = useEmail();

  const { data: policies, isLoading: isPoliciesLoading } = usePolicies(
    party?.id ?? '',
    { activeOnly: true },
    {
      enabled: Boolean(party?.id),
    },
  );

  const { data: policyPackages, isLoading: isPolicyPackagesLoading } =
    usePolicyPackages(party?.id ?? '', undefined, {
      enabled: Boolean(party?.id),
    });

  const shownPolicies = onlyShowRelevant
    ? policies?.filter((policy) => policyIds?.includes(policy.id))
    : policies;

  const filteredPackages = policyPackages?.filter((policyPackage) =>
    hasItems(policyPackage.insurance_policies),
  );

  const shownPackages = onlyShowRelevant
    ? filteredPackages?.filter(
        (policyPackage) =>
          policyPackageIds?.includes(policyPackage.id) ||
          policyPackage.insurance_policies.some((p) =>
            policyIds?.includes(p.id),
          ),
      )
    : filteredPackages;

  const isLoading = isPoliciesLoading || isPolicyPackagesLoading;

  const totalItems = (policies?.length ?? 0) + (filteredPackages?.length ?? 0);

  const relevantItems =
    (shownPolicies?.length ?? 0) + (shownPackages?.length ?? 0);

  return (
    <PartyTabSection
      icon={Shield}
      title={t('POLICIES_TITLE')}
      noItemsMessage={t('POLICY_EMPTY_TITLE')}
      more={generatePath(paths.policies.overview)}
      isLoading={isLoading}
      totalItems={totalItems}
      relevantItems={relevantItems}
    >
      {shownPackages?.map((policyPackage) => (
        <PolicyPackageItem
          key={policyPackage.id}
          policyPackage={policyPackage}
        />
      ))}

      {shownPolicies?.map((policy) => (
        <PolicyItem key={policy.id} policy={policy} />
      ))}
    </PartyTabSection>
  );
}
